import { CardHeader, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import * as api from "api/Core";
import { GlobalClassKeys } from "assets/jss/common-styles";
import BoxDrawer from 'components/BoxDrawer';
import IconButtonDelete from "components/Buttons/IconButtonDelete";
import { RequestIndicator, SpinnerIndicator } from "components/CallServer";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import IconAdd from "components/Icons/IconAdd";
import IconEdit from "components/Icons/IconEdit";
import MuiTextFieldColor from "components/MuiExtras/CustomInput/MuiTextFieldColor";
import { GridEx } from "components/MuiExtras/Grid/GridEx";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiPaper from "components/MuiExtras/MuiPaper";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import AppMessage from "components/Translations/AppMessage";
import { LocationDto, PagedResult } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

/////////////////////////////////////////////////////////////////////////////////////

const MaxTakeRecords = 10;

enum FormMode { Add, Edit }

interface IProps {
    onClose?: (counter: number) => void;
}

interface IState {
    list: PagedResult<LocationDto>;
    mode: FormMode;
    mouseOver: number;
    currentItem: LocationDto;
    inputText: string;
    offset: number;
    lastError: any;
    formCycle: RequestLifeCycle;
}

type Props = IProps & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class LocationEditDialog extends React.Component<Props, IState> {
    private inputRef: any;

    constructor(props: Props) {
        super(props);

        this.state = {
            list: null,
            currentItem: null,
            mode: FormMode.Add,
            mouseOver: null,
            inputText: "",
            offset: 0,
            lastError: null,
            formCycle: RequestLifeCycle.None
        };
    }

    ///
    private handleClose = () => {
        if (this.props.onClose)
            this.props.onClose(this.state.list.total);
    };

    ///
    private handleAdd() {
        this.setState({ formCycle: RequestLifeCycle.Posting });
        api.saveLocation(null, this.state.inputText)
            .then((result) => {
                this.setState(state => {
                    state.list.items.push({ description: state.inputText, locationId: result });
                    state.list.total++;
                    return { ...state, formCycle: RequestLifeCycle.None, inputText: "" };
                });
            })
            .catch((error) => {
                this.setState({ lastError: error, formCycle: RequestLifeCycle.PostFailed });
            });
    }

    ///
    private handleEnterEditMode(item: LocationDto) {
        this.setState({
            mode: FormMode.Edit,
            inputText: item.description,
            currentItem: item
        }, () => this.inputRef.focus());
    }

    ///
    private handleEditComplete() {
        const { currentItem, inputText } = this.state;

        this.setState({ formCycle: RequestLifeCycle.Posting });

        api.saveLocation(currentItem.locationId, inputText)
            .then((result) => {
                if (result) {
                    this.setState(state => {
                        var item = state.list.items.find(p => p.locationId == currentItem.locationId);
                        item.description = inputText;
                        return { ...state, formCycle: RequestLifeCycle.None, inputText: "", mode: FormMode.Add };
                    });
                }
            })
            .catch((error) => {
                this.setState({ lastError: error, formCycle: RequestLifeCycle.PostFailed });
            });
    }

    ///
    private handleDelete(item: LocationDto) {
        this.setState({ formCycle: RequestLifeCycle.Posting });

        api.deleteLocation(item.locationId)
            .then((result) => {
                if (result) this.loadList(this.state.offset);
                else this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
            })
            .catch((error) => {
                this.setState({ lastError: error, formCycle: RequestLifeCycle.PostFailed });
            });
    }


    ///
    private handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ inputText: e.currentTarget.value });
    };

    ///
    private resetInput() {
        this.setState({ mode: FormMode.Add, inputText: "" });
    }

    ///
    private loadList(offset: number) {

        this.setState({ list: null, formCycle: RequestLifeCycle.Loading });

        api.getLocations({ offset, take: MaxTakeRecords })
            .then((result) => {
                this.setState({ list: result, offset: offset });
            })
            .catch((error) => {
                this.setState({ list: null, lastError: error, formCycle: RequestLifeCycle.LoadFailed, offset: 0 });
            });
    }

    ///
    private renderList() {
        const { list } = this.state;

        return (
            list.items.map((item, index) => {
                return (
                    <Grid
                        key={index}
                        container
                        spacing={1}
                        justifyContent="space-between"
                        alignItems="center"
                        wrap="nowrap"
                        className={this.state.mouseOver == index ? GlobalClassKeys.overElementBackground : null}
                        onMouseEnter={() => this.setState({ mouseOver: index })}
                        onMouseLeave={() => this.setState({ mouseOver: null })}
                    >
                        <GridEx item xs zeroMinWidth alignSelf="center">
                            <Typography noWrap>
                                <MuiClickable onClick={() => this.handleEnterEditMode(item)}>{item.description}</MuiClickable>
                            </Typography>
                        </GridEx>
                        <Grid item>
                            <IconButtonDelete size="small" onClick={() => this.handleDelete(item)} />
                        </Grid>
                    </Grid>
                );
            })
        );
    }

    ///
    private renderPager() {
        const { list } = this.state;

        if (list == null || list.total == 0)
            return null;

        return (
            <MuiPagination
                limit={MaxTakeRecords}
                offset={this.state.offset}
                total={this.state.list.total}
                innerButtonCount={3}
                onClick={(e, offset) => this.loadList(offset)}
            />
        );
    }

    ///
    private renderHeader() {
        const color = this.state.mode == FormMode.Add
            ? "warning"
            : "info";

        return (
            <>
                <Grid container={true} alignItems="center" spacing={1}>
                    <Grid item >
                        {
                            this.state.mode == FormMode.Add
                                ? <IconAdd size="2x" color={color} />
                                : <IconEdit size="2x" color={color} />
                        }
                    </Grid>

                    <GridEx item xs>

                        <MuiTextFieldColor
                            fullWidth
                            maxLength={255}
                            autoComplete="off"
                            placeholder={this.props.t("Views.System.Components.LocationEditDialog.InputPlaceholder")}
                            underlineColor={color}
                            value={this.state.inputText}
                            inputRef={(ref) => this.inputRef = ref}
                            onChange={this.handleInputChange}
                            onKeyDown={
                                (e) => {
                                    if (e.keyCode == 13) {
                                        e.preventDefault();

                                        if (this.state.mode == FormMode.Add)
                                            this.handleAdd();
                                        else
                                            this.handleEditComplete();
                                    }
                                    else if (e.keyCode == 27) {
                                        e.preventDefault();
                                        this.resetInput();
                                    }
                                }
                            }
                        />
                    </GridEx>
                </Grid>
            </>
        );
    }

    ///
    public componentDidMount() {
        this.loadList(0);
    }

    ///
    public render() {
        const { list, lastError, formCycle } = this.state;

        return (
            <BoxDrawer
                open
                title={this.props.t("Views.System.Components.LocationEditDialog.BoxTitle")}
                onClose={this.handleClose}
            >

                <MuiPaper dense fullHeight elevation={0}>

                    <RequestIndicator
                        open={formCycle == RequestLifeCycle.Posting}
                        type="post"
                    />

                    <RequestErrorDialog
                        open={formCycle == RequestLifeCycle.PostFailed}
                        error={lastError}
                        onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                    />

                    <GridEx container wrap="nowrap" direction="column" fullHeight>

                        <GridEx item>
                            {this.renderHeader()}
                            <br/>
                        </GridEx>

                        <GridEx item xs overflow="auto">


                            {
                                lastError && formCycle != RequestLifeCycle.PostFailed
                                    ? <RequestErrorReport error={lastError} />
                                    : list == null
                                        ? <SpinnerIndicator />
                                        : list.total == 0
                                            ? <Typography variant="h6">{this.props.t("Views.System.Components.LocationEditDialog.EmptyData")}</Typography>
                                            : this.renderList()
                            }
                        </GridEx>

                        <GridEx item>
                            {this.renderPager()}
                        </GridEx>

                    </GridEx>

                </MuiPaper>

            </BoxDrawer>
        );
    }
}

export default withSnackbar(withTranslation()(LocationEditDialog));